import React, { useState } from 'react';
import { AlertCircle, Calendar, TrendingUp, Package, Target, ChevronRight, ShoppingCart, FileSpreadsheet, Zap, Info } from 'lucide-react';
import { useApi } from '../hooks/useApi';

function ProbabilisticOrderBuilder() {
  const { axiosPost } = useApi();
  // Get tomorrow's date as default
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const [date, setDate] = useState(tomorrowStr);
  const [baseAction, setBaseAction] = useState('');
  const [baseVolume, setBaseVolume] = useState(0);
  const [probModelVersion, setProbModelVersion] = useState('v3.1.0');
  const [trendFilterMargin, setTrendFilterMargin] = useState(10);
  const [fixIModel, setFixIModel] = useState('v2.0.0');
  const [fixIIModel, setFixIIModel] = useState('v1.0.0-fix2');
  const [bucket1Volume, setBucket1Volume] = useState(20);
  const [bucket2Volume, setBucket2Volume] = useState(40);
  const [bucket3Volume, setBucket3Volume] = useState(60);
  const [predsDiffCutoff, setPredsDiffCutoff] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [activeFixing, setActiveFixing] = useState('fixing_i');

  // Fixing II Price Configuration
  const [priceLt500, setPriceLt500] = useState(100);
  const [price500to700, setPrice500to700] = useState(150);
  const [price700to1000, setPrice700to1000] = useState(200);
  const [priceGt1000, setPriceGt1000] = useState(400);


  // Validation: check form validity
  const isValidForm = (() => {
    // Must select a valid action
    if (!baseAction || baseAction === '') {
      return false;
    }
    // If base action is Buy or Sell, base volume must be greater than 0
    if ((baseAction === 'Buy' || baseAction === 'Sell') && baseVolume <= 0) {
      return false;
    }
    return true;
  })();

  const exportToExcel = async () => {
    if (!result || !result.buckets || !date) return;

    try {
      // Call backend to generate file, save to S3, and get file data
      const response = await axiosPost('/export-prob-orders', {
        orders_result: result,
        export_format: 'bos'
      });

      if (response.data.status === 'success') {
        // Decode base64 and trigger download
        const byteCharacters = atob(response.data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log(`File saved to S3: ${response.data.s3_path}`);
      } else {
        setError(response.data.message || 'Failed to export orders');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export orders: ' + (err.message || 'Unknown error'));
    }
  };

  const exportToNobleFormat = async () => {
    if (!result || !result.buckets || !result.date) return;

    try {
      // Call backend to generate file, save to S3, and get file data
      const response = await axiosPost('/export-prob-orders', {
        orders_result: result,
        export_format: 'noble'
      });

      if (response.data.status === 'success') {
        // Decode base64 and trigger download
        const byteCharacters = atob(response.data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log(`File saved to S3: ${response.data.s3_path}`);
      } else {
        setError(response.data.message || 'Failed to export orders');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export orders: ' + (err.message || 'Unknown error'));
    }
  };

  const handleBuildOrder = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      let response;

      if (activeFixing === 'fixing_ii') {
        // First check if Historia file exists
        const checkResponse = await axiosPost('/check-historia-file', { date });
        if (checkResponse.data.status !== 'success' || !checkResponse.data.exists) {
          setError(`No Historia file found for ${date}. Please upload the transaction file first.`);
          setLoading(false);
          return;
        }

        // Use Fixing II Order Builder endpoint
        response = await axiosPost('/build-fix2-orders', {
          date: date,
          base_action: baseAction,
          base_volume: baseVolume,
          price_lt_500: priceLt500,
          price_in_500_700: price500to700,
          price_in_700_1000: price700to1000,
          price_gt_1000: priceGt1000
        });
      } else {
        // Use Probabilistic Order Builder endpoint with bucket configuration
        response = await axiosPost('/build-prob-orders', {
          date: date,
          base_action: baseAction,
          base_volume: baseVolume,
          prob_model_version: probModelVersion,
          trend_filter_margin: trendFilterMargin,
          fix_i_model: fixIModel,
          fix_ii_model: fixIIModel,
          bucket1_volume: bucket1Volume,
          bucket2_volume: bucket2Volume,
          bucket3_volume: bucket3Volume,
          preds_diff_cutoff: predsDiffCutoff
        });
      }

      if (response.data.status === 'success') {
        setResult(response.data);
      } else {
        setError(response.data.message || 'Failed to build orders');
      }
    } catch (err) {
      console.error('Build order error:', err);
      setError(err.message || 'Failed to build orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportFix2ToExcel = async () => {
    if (!result || !result.orders || !date) return;

    try {
      const response = await axiosPost('/export-fix2-orders', {
        orders_result: result
      });

      if (response.data.status === 'success') {
        // Download file logic
        const byteCharacters = atob(response.data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log(`File saved to S3: ${response.data.s3_path}`);
      } else {
        setError(response.data.message || 'Failed to export orders');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export orders: ' + (err.message || 'Unknown error'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Probabilistic Order Builder</h1>
            <p className="text-gray-600">Build trading orders based on probabilistic model predictions</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveFixing('fixing_i')}
              className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                activeFixing === 'fixing_i'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Fixing I
            </button>
            <button
              onClick={() => setActiveFixing('fixing_ii')}
              className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                activeFixing === 'fixing_ii'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Fixing II
            </button>
          </div>
        </div>

        {/* Parameters Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">

          {/* Common fields: Date, Base Action, Base Volume */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Date */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} />
                  <span>Date</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Base Action */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <ShoppingCart size={16} />
                  <span>Base Action</span>
                </label>
              <select
                value={baseAction}
                onChange={(e) => {
                  setBaseAction(e.target.value);
                  // Reset base volume to 0 when switching to Idle
                  if (e.target.value === 'Idle') {
                    setBaseVolume(0);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select action...</option>
                <option value="Buy">Buy</option>
                <option value="Idle">Idle</option>
                <option value="Sell">Sell</option>
              </select>
            </div>

            {/* Base Volume */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Base Volume</span>
              </label>
              <input
                type="number"
                value={baseVolume}
                onChange={(e) => setBaseVolume(parseFloat(e.target.value) || 0)}
                disabled={baseAction === 'Idle' || baseAction === ''}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  baseAction === 'Idle' || baseAction === '' ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
            </div>
          </div>

          {/* Fixing I specific fields */}
          {activeFixing === 'fixing_i' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {/* Probabilistic Model Version */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Zap size={16} />
                      <span>Probabilistic Model Version</span>
                  </label>
                  <select
                    value={probModelVersion}
                    onChange={(e) => setProbModelVersion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="v3.1.0">v3.1.0</option>
                  </select>
                </div>

                {/* Fix I Model */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <TrendingUp size={16}  />
                      <span>Fix I Model</span>
                    </label>
                  <select
                    value={fixIModel}
                    onChange={(e) => setFixIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="v1.0.0">v1.0.0</option>
                    <option value="v2.0.0">v2.0.0</option>
                  </select>
                </div>

                {/* Fix II Model */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <TrendingUp size={16} />
                      <span>Fix II Model</span>
                  </label>
                  <select
                    value={fixIIModel}
                    onChange={(e) => setFixIIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                    <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                  </select>
                </div>

                {/* Preds Diff Cutoff */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <AlertCircle size={16} />
                    <span>Preds Diff Cutoff (%)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={predsDiffCutoff}
                    onChange={(e) => setPredsDiffCutoff(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Trend Filter Margin */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Target size={16} />
                      <span>Trend Filter Margin (PLN)</span>
                  </label>
                  <input
                    type="number"
                    value={trendFilterMargin}
                    onChange={(e) => setTrendFilterMargin(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Bucket Configuration */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Bucket Configuration</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <Package size={16} />
                        <span>Bucket 1 Volume (MWh)</span>
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={bucket1Volume}
                        onChange={(e) => setBucket1Volume(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <Package size={16} />
                        <span>Bucket 2 Volume (MWh)</span>
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={bucket2Volume}
                        onChange={(e) => setBucket2Volume(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <Package size={16} />
                        <span>Bucket 3 Volume (MWh)</span>
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={bucket3Volume}
                        onChange={(e) => setBucket3Volume(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
              </div>
            </>
          )}

          {/* Fixing II specific fields */}
          {activeFixing === 'fixing_ii' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Margin Configuration</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <span>Price &lt; 500</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={priceLt500}
                    onChange={(e) => setPriceLt500(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <span>Price in 500-700</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={price500to700}
                    onChange={(e) => setPrice500to700(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <span>Price in 700-1000</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={price700to1000}
                    onChange={(e) => setPrice700to1000(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <span>Price &gt; 1000</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={priceGt1000}
                    onChange={(e) => setPriceGt1000(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Run Button */}
          <div className="mt-6">
            <button
              onClick={handleBuildOrder}
              disabled={loading || !isValidForm}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Building Orders...</span>
                </>
              ) : (
                <>
                  <ChevronRight className="w-5 h-5" />
                  <span>Build Order</span>
                </>
              )}
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && result.buckets && (
          <>
            {/* Metadata Summary - Compact */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-6">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Orders for {result.date}
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">Probabilistic: <span className="font-medium text-gray-800">{result.metadata.avg_q50?.toFixed(2) || '-'}</span></span>
                    <span className="text-gray-500">Fix I: <span className="font-medium text-gray-800">{result.metadata.avg_fix_i?.toFixed(2) || '-'}</span></span>
                    <span className="text-gray-500">Fix II: <span className="font-medium text-gray-800">{result.metadata.avg_fix_ii?.toFixed(2) || '-'}</span></span>
                    <span className="text-gray-500">Diff: <span className={`font-medium ${result.metadata.preds_diff > 0 ? 'text-red-600' : result.metadata.preds_diff < 0 ? 'text-green-600' : 'text-gray-800'}`}>{result.metadata.preds_diff !== null ? `${result.metadata.preds_diff}%` : '-'}</span></span>
                  </div>
                  {result.metadata.skip_buy && (
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">BUY skipped</span>
                  )}
                  {result.metadata.skip_sell && (
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">SELL skipped</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={exportToExcel}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export in BOŚ format
                  </button>
                  <button
                    onClick={exportToNobleFormat}
                    className="px-3 py-1.5 bg-orange-300 text-gray-800 text-sm font-medium rounded-lg hover:bg-orange-400 flex items-center gap-1.5 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export in Noble format
                  </button>
                </div>
              </div>
            </div>

            {/* Combined Orders Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-4 text-xs">
                {result.metadata.base_action !== 'Idle' && (
                  <span className="text-blue-700 font-medium">BASE: {result.buckets.base.volume} MWh</span>
                )}
                <span className="text-green-700 font-medium">Bucket 1 ({result.buckets.bucket1.volume} MWh)</span>
                <span className="text-yellow-700 font-medium">Bucket 2 ({result.buckets.bucket2.volume} MWh)</span>
                <span className="text-orange-700 font-medium">Bucket 3 ({result.buckets.bucket3.volume} MWh)</span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Hour</th>
                      {result.metadata.base_action !== 'Idle' && (
                        <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50">BASE</th>
                      )}
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">Bucket 1 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">Bucket 1 Sell</th>
                      <th className="px-2 py-1.5 text-right font-medium text-yellow-700 bg-yellow-50">Bucket 2 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-yellow-700 bg-yellow-50">Bucket 2 Sell</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">Bucket 3 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">Bucket 3 Sell</th>
                      <th className="px-2 py-1.5 text-center font-medium text-gray-600">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.buckets.bucket1.orders.map((b1Order, index) => {
                      const baseOrder = result.buckets.base?.orders?.find(o => o.hour === b1Order.hour);
                      const b2Order = result.buckets.bucket2?.orders?.find(o => o.hour === b1Order.hour);
                      const b3Order = result.buckets.bucket3?.orders?.find(o => o.hour === b1Order.hour);

                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 py-1 text-gray-900 font-medium sticky left-0 bg-white z-10">{displayHours1to24 ? b1Order.hour + 1 : b1Order.hour}</td>
                          {result.metadata.base_action !== 'Idle' && baseOrder && (
                            <td className={`px-2 py-1 text-right bg-blue-50/50 ${baseOrder.action === 'Buy' ? 'text-green-700' : 'text-red-700'}`}>
                              {baseOrder.price.toFixed(2)}
                            </td>
                          )}
                          <td className={`px-2 py-1 text-right bg-green-50/30 ${b1Order.skip_buy ? 'text-gray-400 line-through' : 'text-green-700'}`}>
                            {b1Order.buy_price.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-green-50/30 ${b1Order.skip_sell ? 'text-gray-400 line-through' : 'text-red-700'}`}>
                            {b1Order.sell_price.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-yellow-50/30 ${b2Order?.skip_buy ? 'text-gray-400 line-through' : 'text-green-700'}`}>
                            {b2Order?.buy_price.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-yellow-50/30 ${b2Order?.skip_sell ? 'text-gray-400 line-through' : 'text-red-700'}`}>
                            {b2Order?.sell_price.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${b3Order?.skip_buy ? 'text-gray-400 line-through' : 'text-green-700'}`}>
                            {b3Order?.buy_price.toFixed(2)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${b3Order?.skip_sell ? 'text-gray-400 line-through' : 'text-red-700'}`}>
                            {b3Order?.sell_price.toFixed(2)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {b1Order.shift_direction === -1 ? <span className="text-green-600">up</span> : b1Order.shift_direction === 1 ? <span className="text-red-600">down</span> : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Fixing II Results */}
        {result && result.orders && activeFixing === 'fixing_ii' && (
          <>
            {/* Metadata Summary */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-6">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Fix II Orders for {result.date}
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">Base Action: <span className="font-medium text-gray-800">{result.base_action}</span></span>
                    {result.base_volume > 0 && (
                      <span className="text-gray-500">Base Volume: <span className="font-medium text-gray-800">{result.base_volume} MWh</span></span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={exportFix2ToExcel}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            </div>

            {/* Fix II Orders Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-100">
                      <th className="px-2 py-1 sticky left-0 bg-gray-50 z-10"></th>
                      <th colSpan="3" className="px-2 py-1 text-center font-semibold text-blue-800 bg-blue-50 border-l border-r border-blue-100">Fixing I Transactions</th>
                      <th colSpan="5" className="px-2 py-1 text-center font-semibold text-purple-800 bg-purple-50 border-r border-purple-100">Proposed Fixing II Orders</th>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Hour</th>
                      <th className="px-2 py-1.5 text-center font-medium text-blue-700 bg-blue-50 border-l border-blue-100">Action</th>
                      <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50">Vol</th>
                      <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50 border-r border-blue-100">Price</th>
                      <th className="px-2 py-1.5 text-left font-medium text-purple-700 bg-purple-50">Hour</th>
                      <th className="px-2 py-1.5 text-center font-medium text-purple-700 bg-purple-50">Action</th>
                      <th className="px-2 py-1.5 text-right font-medium text-purple-700 bg-purple-50">Vol</th>
                      <th className="px-2 py-1.5 text-right font-medium text-purple-700 bg-purple-50">Price</th>
                      <th className="px-2 py-1.5 text-right font-medium text-purple-700 bg-purple-50 border-r border-purple-100">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.orders.map((order, index) => {
                      // Format volume: show 1 decimal only if not .0
                      const formatVol = (vol) => {
                        if (!vol || vol <= 0) return '-';
                        return vol % 1 === 0 ? vol.toString() : vol.toFixed(1);
                      };
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 py-1 text-gray-900 font-medium sticky left-0 bg-white z-10">
                            {displayHours1to24 ? order.hour : order.hour - 1}
                          </td>
                          <td className={`px-2 py-1 text-center bg-blue-50/30 border-l border-blue-100 ${order.fix_i_action === 'Buy' ? 'text-green-700' : order.fix_i_action === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.fix_i_action || '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-blue-50/30 text-gray-700">
                            {formatVol(order.fix_i_volume)}
                          </td>
                          <td className="px-2 py-1 text-right bg-blue-50/30 text-gray-700 border-r border-blue-100">
                            {order.fix_i_price != null ? order.fix_i_price.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 py-1 text-gray-900 font-medium bg-purple-50/30">
                            {displayHours1to24 ? order.hour : order.hour - 1}
                          </td>
                          <td className={`px-2 py-1 text-center bg-purple-50/30 font-medium ${order.action === 'Buy' ? 'text-green-700' : order.action === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.action || '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-purple-50/30 text-gray-700 font-medium">
                            {formatVol(order.volume)}
                          </td>
                          <td className={`px-2 py-1 text-right bg-purple-50/30 font-medium ${order.action === 'Buy' ? 'text-green-700' : order.action === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.price != null ? order.price.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-purple-50/30 text-gray-600 border-r border-purple-100">
                            {order.margin_applied > 0 ? order.margin_applied : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProbabilisticOrderBuilder;
